import { formatDuration, riyadhTime, type InspectionType } from '@room-verify/shared';
import type { ActiveInspection, MyWork } from '../api';
import {
  Button,
  IconAlert,
  IconArrowRight,
  IconCheckCircle,
  IconClock,
  IconDoor,
  IconSparkle,
} from '../components';
import type { Copy } from '../i18n';

export function HomeScreen({
  copy,
  work,
  loading,
  active,
  onChooseType,
  onResume,
  onDiscardActive,
}: {
  copy: Copy;
  work: MyWork | null;
  loading: boolean;
  active: ActiveInspection | null;
  onChooseType: (type: InspectionType) => void;
  onResume: () => void;
  onDiscardActive: () => void;
}) {
  return (
    <div className="screen">
      {active && (
        <section className="resume-card" aria-labelledby="resume-title">
          <span className="resume-icon" aria-hidden="true">
            <IconAlert size={18} />
          </span>
          <div className="resume-body">
            <h2 id="resume-title" className="resume-title">
              {copy.resumeTitle}
            </h2>
            <p className="resume-text">{copy.resumeText(active.roomNumber)}</p>
            <div className="resume-actions">
              <Button size="sm" variant="accent" onClick={onResume}>
                {copy.resumeAction}
              </Button>
              <Button size="sm" variant="ghost" onClick={onDiscardActive}>
                {copy.discardAction}
              </Button>
            </div>
          </div>
        </section>
      )}

      <section aria-labelledby="choose-title">
        <h2 id="choose-title" className="section-title">
          {copy.chooseInspection}
        </h2>
        <div className="type-grid">
          <button className="type-card" onClick={() => onChooseType('CHECKOUT')}>
            <span className="type-icon type-icon-checkout" aria-hidden="true">
              <IconDoor size={22} />
            </span>
            <span className="type-body">
              <span className="type-name">{copy.checkoutRooms}</span>
              <span className="type-hint">{copy.checkoutHint}</span>
            </span>
            <IconArrowRight size={18} className="type-chevron" />
          </button>

          <button className="type-card" onClick={() => onChooseType('CLEAN_ROOM')}>
            <span className="type-icon type-icon-clean" aria-hidden="true">
              <IconSparkle size={22} />
            </span>
            <span className="type-body">
              <span className="type-name">{copy.cleanRooms}</span>
              <span className="type-hint">{copy.cleanHint}</span>
            </span>
            <IconArrowRight size={18} className="type-chevron" />
          </button>
        </div>
      </section>

      <section aria-labelledby="work-title">
        <h2 id="work-title" className="section-title">
          {copy.myWorkToday}
        </h2>

        {loading && !work ? (
          <div className="stat-row">
            {[0, 1, 2].map((index) => (
              <div key={index} className="stat-card">
                <div className="skeleton" style={{ height: 26, width: '48%' }} />
                <div className="skeleton" style={{ height: 12, width: '72%', marginTop: 8 }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="stat-row">
            <div className="stat-card">
              <span className="stat-value num">{work?.stats.total ?? 0}</span>
              <span className="stat-label">{copy.totalCompleted}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value num">{work?.stats.checkout ?? 0}</span>
              <span className="stat-label">{copy.checkout}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value num">{work?.stats.clean ?? 0}</span>
              <span className="stat-label">{copy.clean}</span>
            </div>
          </div>
        )}

        {loading && !work ? (
          <ul className="work-list">
            {[0, 1].map((index) => (
              <li key={index} className="work-item">
                <div className="skeleton" style={{ height: 44, width: '100%' }} />
              </li>
            ))}
          </ul>
        ) : work && work.results.length > 0 ? (
          <ul className="work-list">
            {work.results.map((item) => (
              <li key={item.id} className="work-item">
                <span className="work-check" aria-hidden="true">
                  <IconCheckCircle size={18} />
                </span>
                <div className="work-main">
                  <div className="work-top">
                    <span className="work-room">
                      {copy.roomNumber} <strong className="num">{item.roomNumber}</strong>
                    </span>
                    <span
                      className={`badge ${
                        item.inspectionType === 'CHECKOUT' ? 'badge-neutral' : 'badge-accent'
                      }`}
                    >
                      {item.inspectionType === 'CHECKOUT' ? copy.checkout : copy.clean}
                    </span>
                  </div>
                  <div className="work-meta">
                    <span>{copy.completedAt(riyadhTime(item.completedAt))}</span>
                    <span className="work-dot" aria-hidden="true">
                      ·
                    </span>
                    <span className="work-duration">
                      <IconClock size={13} />
                      <span className="num">{formatDuration(item.durationSeconds)}</span>
                    </span>
                    {!item.itemsComplete && (
                      <>
                        <span className="work-dot" aria-hidden="true">
                          ·
                        </span>
                        <span className="work-missing">{copy.missingItems}</span>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty card">
            <span className="empty-icon">
              <IconDoor size={22} />
            </span>
            <span className="empty-title">{copy.noWorkTitle}</span>
            <span className="empty-text">{copy.noWorkText}</span>
          </div>
        )}
      </section>
    </div>
  );
}
